import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2 } from "lucide-react";
import AdminThemeToggle from "@/components/AdminThemeToggle";
import { statusSv, methodSv, statusBadgeCls, methodBadgeCls } from "@/lib/adminLabels";
import { devWarn } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOrders() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

  useEffect(() => { if (!authLoading && !user) navigate("/admin/login"); }, [user, authLoading, navigate]);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: getHeaders() });
      setOrders(res.data);
    } catch (err) { devWarn("Failed to fetch orders:", err.message); } finally { setLoading(false); }
  };

  useEffect(() => { if (user) fetchOrders(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const updateStatus = async (orderNumber, newStatus) => {
    await axios.put(`${API}/admin/orders/${orderNumber}`, { payment_status: newStatus }, { headers: getHeaders() });
    toast.success(`Order ${orderNumber} uppdaterad till ${statusSv(newStatus)}`);
    fetchOrders();
  };

  const deleteOrder = async (orderNumber) => {
    if (!window.confirm(`Radera order ${orderNumber}? Detta går inte att ångra.`)) return;
    await axios.delete(`${API}/admin/orders/${orderNumber}`, { headers: getHeaders() });
    toast.success(`Order ${orderNumber} raderad`);
    fetchOrders();
  };

  const filtered = filter === "all" ? orders : orders.filter(o => o.payment_status === filter);

  if (authLoading || !user) return <div className="min-h-screen bg-[#0A0A0A]" />;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-orders-page">
      <nav className="bg-[#141414] border-b border-[#262626] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/admin" className="text-[#C5A059] font-semibold text-sm tracking-[0.3em] uppercase font-['Outfit']">SERAVA ADMIN</Link>
            <div className="hidden md:flex gap-6">
              <Link to="/admin" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Översikt</Link>
              <Link to="/admin/events" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Events</Link>
              <Link to="/admin/pages" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Sidor</Link>
              <Link to="/admin/orders" className="text-white text-xs tracking-wider uppercase font-['Outfit']">Ordrar</Link>
              <Link to="/admin/contest-entries" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Tävling</Link>
              <Link to="/admin/settings" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Inställningar</Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/40 hover:text-white text-xs font-['Outfit']">Visa sajten</Link>
            <AdminThemeToggle />
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="text-white/40 hover:text-red-400"><LogOut size={18} /></button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-['Playfair_Display'] text-3xl text-white mb-6">Ordrar</h1>

        <div className="flex gap-2 mb-6 flex-wrap">
          {["all", "paid", "awaiting_invoice", "initiated", "pending"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-2 text-xs tracking-wider uppercase font-['Outfit'] border transition-colors ${filter === f ? 'border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5' : 'border-[#262626] text-white/50 hover:text-white'}`}>
              {f === "all" ? "Alla" : statusSv(f)} ({f === "all" ? orders.length : orders.filter(o => o.payment_status === f).length})
            </button>
          ))}
        </div>

        <div className="bg-[#141414] border border-[#262626] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-['Outfit']">
              <thead><tr className="border-b border-[#262626] text-white/50 text-xs uppercase tracking-wider">
                <th className="text-left p-4">Order #</th><th className="text-left p-4">Datum</th><th className="text-left p-4">Kund</th>
                <th className="text-left p-4">Event</th><th className="text-left p-4">Paket</th><th className="text-left p-4">Antal</th>
                <th className="text-left p-4">Totalt</th><th className="text-left p-4">Betalsätt</th><th className="text-left p-4">Status</th><th className="text-left p-4">Åtgärder</th>
              </tr></thead>
              <tbody>
                {filtered.map(o => (
                  <tr key={o.order_number} className="border-b border-[#262626]/50 hover:bg-white/[0.02]">
                    <td className="p-4 text-[#C5A059] font-medium whitespace-nowrap">{o.order_number}</td>
                    <td className="p-4 text-white/40 whitespace-nowrap">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                    <td className="p-4">
                      <p className="text-white/70">{o.customer_name}</p>
                      <p className="text-white/30 text-xs">{o.customer_email}</p>
                    </td>
                    <td className="p-4 text-white/50">{o.event_title}</td>
                    <td className="p-4 text-white/50">{o.package_name}</td>
                    <td className="p-4 text-white/50">{o.quantity}</td>
                    <td className="p-4 text-white font-medium whitespace-nowrap">{o.total_amount?.toLocaleString()} {o.currency}</td>
                    <td className="p-4"><span className={`text-xs px-2 py-1 border ${methodBadgeCls(o.payment_method)}`}>{methodSv(o.payment_method)}</span></td>
                    <td className="p-4"><span className={`text-xs px-2 py-1 border ${statusBadgeCls(o.payment_status)}`}>{statusSv(o.payment_status)}</span></td>
                    <td className="p-4">
                      <div className="flex gap-1 items-center">
                        {o.payment_status === "awaiting_invoice" && (
                          <>
                            <button onClick={() => updateStatus(o.order_number, "invoice_sent")} className="text-xs px-2 py-1 border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 transition-colors">Markera skickad</button>
                            <button onClick={() => updateStatus(o.order_number, "paid")} className="text-xs px-2 py-1 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 transition-colors">Markera betald</button>
                          </>
                        )}
                        {o.payment_status === "invoice_sent" && (
                          <button onClick={() => updateStatus(o.order_number, "paid")} className="text-xs px-2 py-1 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 transition-colors">Markera betald</button>
                        )}
                        <button onClick={() => deleteOrder(o.order_number)} data-testid={`delete-order-${o.order_number}`} className="text-xs px-2 py-1 border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1 ml-1" title="Radera order">
                          <Trash2 size={12} /> Radera
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && !loading && <tr><td colSpan="10" className="p-8 text-center text-white/30">Inga ordrar funna</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
